import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Link,
  Button,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { buildDownloadUrl } from '../../helpers/apiUtils';

interface DecisionPath {
  chain_type: string;
  tiers_before_match: string[];
  winning_tier: number;
  winning_tier_name: string;
}

interface Insertion {
  id: number;
  newsletter_type: string;
  newsletter_date: string | null;
  article_id: string;
  article_title: string;
  isin: string | null;
  ad_source: string;
  ad_copy: string;
  ad_link: string | null;
  banner: string | null;
  llm_model: string | null;
  ad_details: Record<string, any>;
  decision_path: DecisionPath;
  created_at: string | null;
}

const AD_SOURCE_LABELS: Record<string, string> = {
  webinar_default: 'Live Webinar',
  webinar_evergreen: 'Evergreen Webinar',
  plx_offer_time_limited: 'Direkt-Werbung',
  plx_offer_evergreen: 'PLX Evergreen',
  landing_page_fallback: 'VNR Fallback',
  landing_page_direct: 'VNR Direkt',
};

const TIER_SKIP_LABELS: Record<string, string> = {
  webinar_default_skip: 'Live Webinar',
  plx_time_limited_skip: 'Direkt-Werbung',
  webinar_evergreen_skip: 'Evergreen Webinar',
  plx_evergreen_skip: 'PLX Evergreen',
};

interface Props {
  open: boolean;
  onClose: () => void;
  insertion: Insertion | null;
}

const NewsletterAdInsertionDetailModal: React.FC<Props> = ({ open, onClose, insertion }) => {
  if (!insertion) return null;

  const handleCopyBanner = () => {
    if (insertion.banner) {
      navigator.clipboard.writeText(insertion.banner);
    }
  };

  const renderAdDetails = (details: Record<string, any>) => {
    if (!details || Object.keys(details).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Keine Details
        </Typography>
      );
    }

    const skipKeys = ['url', 'offer_url'];

    return (
      <Box>
        {Object.entries(details)
          .filter(([key]) => !skipKeys.includes(key))
          .map(([key, value]) => {
            if (key === 'considered_webinars' && Array.isArray(value)) {
              return (
                <Box key={key} mb={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {key}:
                  </Typography>
                  {value.map((w: any, i: number) => (
                    <Typography key={i} variant="body2" sx={{ pl: 2 }}>
                      {w.title} (ID: {w.id})
                    </Typography>
                  ))}
                </Box>
              );
            }

            return (
              <Box key={key} display="flex" gap={1} mb={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                  {key}:
                </Typography>
                <Typography variant="body2">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-')}
                </Typography>
              </Box>
            );
          })}

        {/* Show URLs separately as links */}
        {skipKeys.map((key) =>
          details[key] ? (
            <Box key={key} display="flex" gap={1} mb={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                {key}:
              </Typography>
              <Link
                href={details[key]}
                target="_blank"
                rel="noopener"
                variant="body2"
                sx={{ wordBreak: 'break-all' }}
              >
                {details[key]}
              </Link>
            </Box>
          ) : null,
        )}
      </Box>
    );
  };

  const renderDecisionPath = (dp: DecisionPath) => {
    return (
      <Box>
        <Box display="flex" gap={1} mb={1} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
            Chain Type:
          </Typography>
          <Chip label={dp.chain_type} size="small" variant="outlined" />
        </Box>

        {dp.tiers_before_match.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" color="text.secondary">
              Skipped Tiers:
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
              {dp.tiers_before_match.map((tier, i) => (
                <Chip
                  key={i}
                  label={TIER_SKIP_LABELS[tier] || tier}
                  size="small"
                  sx={{
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    fontSize: 11,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
            Winner:
          </Typography>
          <Chip
            label={`Tier ${dp.winning_tier}: ${dp.winning_tier_name}`}
            size="small"
            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Ad Insertion Detail</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {insertion.newsletter_type === 'finanztrends' && insertion.newsletter_date ? (
            <Tooltip title="Öffnet den gespeicherten Newsletter-Send (post-AI-Injection, post-Affiliate-Replace) in neuem Tab. Quelle: ft_newsletter_mailings.inner_html — nur für FT-Sends nach 2026-05-05 verfügbar.">
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  const url = buildDownloadUrl('/api/newsletter-ad-insertions/preview', {
                    newsletter_date: insertion.newsletter_date as string,
                    newsletter_type: insertion.newsletter_type,
                  });
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                Newsletter Vorschau
              </Button>
            </Tooltip>
          ) : null}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Header info */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Newsletter
            </Typography>
            <Typography variant="body1">{insertion.newsletter_type}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Datum
            </Typography>
            <Typography variant="body1">{insertion.newsletter_date || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Ad Quelle
            </Typography>
            <Typography variant="body1">
              {AD_SOURCE_LABELS[insertion.ad_source] || insertion.ad_source}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              LLM
            </Typography>
            <Typography variant="body1">{insertion.llm_model || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Erstellt
            </Typography>
            <Typography variant="body1">
              {insertion.created_at
                ? new Date(insertion.created_at).toLocaleString('de-DE', {
                    timeZone: 'Europe/Berlin',
                  })
                : '-'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Article */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Artikel
          </Typography>
          <Typography variant="body2">{insertion.article_title}</Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {insertion.article_id} {insertion.isin ? `| ISIN: ${insertion.isin}` : ''}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Banner */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Banner
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                p: 1,
                borderRadius: 1,
                wordBreak: 'break-all',
              }}
            >
              {insertion.banner || '-'}
            </Typography>
            {insertion.banner && (
              <IconButton size="small" onClick={handleCopyBanner} title="Kopieren">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Ad Copy */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Ad Text
          </Typography>
          <Typography
            variant="body2"
            sx={{
              backgroundColor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
            }}
          >
            {insertion.ad_copy || '-'}
          </Typography>
          {insertion.ad_link && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                Link:{' '}
              </Typography>
              <Link
                href={insertion.ad_link}
                target="_blank"
                rel="noopener"
                variant="caption"
                sx={{ wordBreak: 'break-all' }}
              >
                {insertion.ad_link}
              </Link>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Decision Path */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Decision Path
          </Typography>
          {renderDecisionPath(insertion.decision_path)}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Ad Details */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Ad Details
          </Typography>
          {renderAdDetails(insertion.ad_details)}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterAdInsertionDetailModal;
